import React, { useState, useRef, useEffect } from 'react';
import JoditEditor from 'jodit-react';
import {
    MdCheck,
    MdEdit,
    MdFactCheck,
    MdRefresh,
    MdWarningAmber,
    MdAutoFixHigh,
    MdDocumentScanner,
} from 'react-icons/md';
import { useAuth } from '../../../Context/AuthContext';
import { getAiSourceType } from '../AegisAiUi';
import { cleanAegisAiTranscript } from '../../../Services/AegisAiDocumentsService';
import { toast } from 'react-toastify';
import '../../../Styles/TextEditor.css';

function formatContentToHtml(raw) {
    if (!raw || typeof raw !== 'string') return '';
    const hasHtmlTags = /<[a-z][\s\S]*>/i.test(raw);
    if (!hasHtmlTags) {
        return raw
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .split(/\n{2,}/)
            .map((p) => `<p>${p.replace(/\n/g, '<br/>')}</p>`)
            .join('');
    }
    return raw;
}

function getCounts(text) {
    if (!text || typeof text !== 'string') return { words: 0, chars: 0 };
    const plain = text.replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').trim();
    if (!plain) return { words: 0, chars: 0 };
    const words = plain.split(/\s+/).filter(Boolean).length;
    return { words, chars: plain.length };
}

const EXTRACTED_JODIT_CONFIG = {
    readonly: false,
    placeholder: 'Edit extracted content...',
    hidePoweredByJodit: true,
    askBeforePasteHTML: false,
    askBeforePasteFromWord: false,
    defaultActionOnPaste: 'insert_as_html',
    defaultActionOnPasteFromWord: 'insert_as_html',
    processPasteHTML: true,
    showXPathInStatusbar: false,
    showCharsCounter: true,
    showWordsCounter: true,
    toolbarAdaptive: false,
    height: '100%',
    minHeight: 380,
    removeButtons: [
        'link', 'hr', 'symbol', 'math', 'video', 'file',
        'speechRecognize', 'print', 'about', 'fullsize', 'preview', 'classSpan',
        'superscript', 'subscript', 'lineHeight', 'font', 'copyformat'
    ],
    disablePlugins: [
        'about', 'powered-by-jodit', 'poweredByJodit', 'symbol', 'math', 'video', 'file', 'link'
    ],
    buttons: [
        'bold', 'italic', 'underline', 'strikethrough', 'eraser', '|',
        'ul', 'ol', '|',
        'paragraph', 'fontsize', '|',
        'image', 'table', '|',
        'brush', '|',
        'left', 'center', 'right', 'justify', '|',
        'undo', 'redo', '|',
        'source',
    ],
};

export default function AiExtractedContentEditor({
    sourceType,
    text,
    initialText,
    onTextChange,
    onReset,
    disabled = false,
    token: propToken,
    isOcr = false,
}) {
    const { token: contextToken } = useAuth();
    const token = propToken || contextToken;
    const type = getAiSourceType(sourceType);

    const [isEditing, setIsEditing] = useState(false);
    const [editorValue, setEditorValue] = useState(text || '');

    const isTranscriptSource = ['video', 'audio', 'youtube'].includes(sourceType);
    const [cleaning, setCleaning] = useState(false);
    const [cleanedVersion, setCleanedVersion] = useState(null);
    const [rawOriginalVersion, setRawOriginalVersion] = useState(null);
    const [activeVersion, setActiveVersion] = useState('current');

    const joditRef = useRef(null);
    const previewRef = useRef(null);

    // Sync editor value if prop updates externally
    useEffect(() => {
        const val = text || '';
        setEditorValue(val);
    }, [text]);

    const handleStartEditing = () => {
        const formatted = formatContentToHtml(text || initialText || '');
        setEditorValue(formatted);
        setIsEditing(true);
    };

    const handleDoneEditing = () => {
        let latest = null;
        if (joditRef.current) {
            if (typeof joditRef.current.getEditorValue === 'function') {
                latest = joditRef.current.getEditorValue();
            } else if (typeof joditRef.current.value === 'string') {
                latest = joditRef.current.value;
            }
        }
        const finalVal = latest !== null ? latest : editorValue;
        setEditorValue(finalVal);
        onTextChange?.(finalVal);
        setIsEditing(false);
    };

    const handleBlur = (newContent) => {
        setEditorValue(newContent);
        onTextChange?.(newContent);
    };

    const handleCleanTranscript = async () => {
        const textToClean = text || initialText || '';
        if (!textToClean.trim()) {
            toast.warn('No transcript text available to clean.');
            return;
        }
        try {
            setCleaning(true);
            const res = await cleanAegisAiTranscript(token, textToClean, sourceType);
            if (res?.is_success && res?.data?.cleaned_text) {
                const cleaned = res.data.cleaned_text;
                const orig = res.data.original_text || textToClean;
                setRawOriginalVersion(orig);
                setCleanedVersion(cleaned);
                setActiveVersion('cleaned');

                const cleanedHtml = formatContentToHtml(cleaned);
                setEditorValue(cleanedHtml);
                onTextChange?.(cleaned);
                toast.success('Transcript successfully cleaned & paraphrased! Spoken fillers and stutters removed.');
            } else {
                toast.error(res?.message || 'Could not clean transcript');
            }
        } catch (err) {
            console.error('Transcript cleanup error:', err);
            toast.error(err?.message || 'An error occurred during transcript cleanup');
        } finally {
            setCleaning(false);
        }
    };

    const handleSwitchVersion = (version) => {
        if (version === 'cleaned' && cleanedVersion) {
            setActiveVersion('cleaned');
            const formatted = formatContentToHtml(cleanedVersion);
            setEditorValue(formatted);
            onTextChange?.(cleanedVersion);
        } else if (version === 'original' && rawOriginalVersion) {
            setActiveVersion('original');
            const formatted = formatContentToHtml(rawOriginalVersion);
            setEditorValue(formatted);
            onTextChange?.(rawOriginalVersion);
        }
    };

    const handleReset = () => {
        onReset?.();
    };

    const isModified = Boolean(text && initialText && text.trim() !== initialText.trim());
    const counts = getCounts(text || initialText || '');
    const displayHtml = formatContentToHtml(text || initialText || '');

    return (
        <div className="hc-ai-extracted-editor rounded-4 border mt-3 overflow-hidden">
            {/* Header */}
            <div className="d-flex flex-wrap align-items-center justify-content-between p-3 border-bottom hc-ai-xe-bar gap-2">
                <div className="d-flex align-items-center gap-2" style={{ minWidth: 0 }}>
                    <span
                        className="d-inline-flex align-items-center justify-content-center rounded-3 text-white flex-shrink-0"
                        style={{ width: 34, height: 34, background: type.color }}
                    >
                        <MdFactCheck size={20} />
                    </span>
                    <div style={{ minWidth: 0 }}>
                        <div className="d-flex align-items-center gap-2 flex-wrap">
                            <span className="fw-bold hc-text-primary" style={{ fontSize: '0.96rem' }}>
                                Extracted &amp; Reviewed Content
                            </span>
                            <span className="badge rounded-pill fw-semibold hc-ai-xe-badge is-teal">
                                Latest from DB
                            </span>
                            {sourceType === 'document' ? (
                                isOcr ? (
                                    <span
                                        className="badge rounded-pill fw-semibold hc-ai-xe-badge d-inline-flex align-items-center gap-1"
                                        style={{ backgroundColor: '#e0f2fe', color: '#0284c7', border: '1px solid #bae6fd' }}
                                        title="Extracted with Vision AI OCR enabled"
                                    >
                                        <MdDocumentScanner size={12} />
                                        <span>OCR Enabled</span>
                                    </span>
                                ) : (
                                    <span
                                        className="badge rounded-pill fw-semibold hc-ai-xe-badge"
                                        style={{ backgroundColor: '#f1f5f9', color: '#64748b', border: '1px solid #e2e8f0' }}
                                        title="Standard extraction without OCR"
                                    >
                                        OCR Disabled
                                    </span>
                                )
                            ) : null}
                            {activeVersion === 'cleaned' ? (
                                <span className="badge rounded-pill fw-semibold hc-ai-xe-badge is-success">
                                    Paraphrased &amp; Cleaned
                                </span>
                            ) : null}
                            {isModified ? (
                                <span className="badge rounded-pill fw-semibold hc-ai-xe-badge is-warning">
                                    Edited (Unsaved)
                                </span>
                            ) : null}
                        </div>
                        <small className="hc-ai-xe-muted d-block text-truncate">
                            {isEditing
                                ? 'Edit the content below using the rich editor. Click Done (Preview) to review changes.'
                                : `Review and edit the indexed content from this ${type.label.toLowerCase()}.`}
                        </small>
                    </div>
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="hc-ai-xe-muted small me-1 d-none d-sm-inline">
                        {counts.words.toLocaleString()} words · {counts.chars.toLocaleString()} chars
                    </span>

                    {/* Clean & Paraphrase Transcript Button for Video, Audio, and YouTube */}
                    {isTranscriptSource ? (
                        <div className="d-flex align-items-center gap-1">
                            <button
                                type="button"
                                className={`hc-ai-rv-btn d-inline-flex align-items-center gap-1 fw-semibold ${activeVersion === 'cleaned' ? 'is-cleaned' : ''}`}
                                onClick={handleCleanTranscript}
                                disabled={disabled || cleaning}
                                title="Clean up and paraphrase transcript: removes speech fillers and repetitive stutters"
                            >
                                {cleaning ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm" role="status" style={{ width: 13, height: 13 }} />
                                        <span>Cleaning...</span>
                                    </>
                                ) : (
                                    <>
                                        <MdAutoFixHigh size={16} className="hc-ai-xe-ok" />
                                        <span>Clean &amp; Paraphrase</span>
                                    </>
                                )}
                            </button>

                            {cleanedVersion && rawOriginalVersion && !isEditing ? (
                                <div className="hc-ai-rv-seg d-inline-flex rounded-pill flex-shrink-0" role="group" aria-label="Transcript version">
                                    <button
                                        type="button"
                                        className={`rounded-pill ${activeVersion === 'cleaned' ? 'is-active' : ''}`}
                                        onClick={() => handleSwitchVersion('cleaned')}
                                        disabled={disabled || cleaning}
                                    >
                                        Cleaned
                                    </button>
                                    <button
                                        type="button"
                                        className={`rounded-pill ${activeVersion === 'original' ? 'is-active' : ''}`}
                                        onClick={() => handleSwitchVersion('original')}
                                        disabled={disabled || cleaning}
                                    >
                                        Original
                                    </button>
                                </div>
                            ) : null}
                        </div>
                    ) : null}

                    {isModified && onReset && !isEditing ? (
                        <button
                            type="button"
                            className="hc-ai-rv-btn d-inline-flex align-items-center gap-1"
                            onClick={handleReset}
                            disabled={disabled}
                            title="Reset to original database content"
                        >
                            <MdRefresh size={15} />
                            <span>Reset</span>
                        </button>
                    ) : null}

                    <button
                        type="button"
                        className={`hc-ai-rv-btn d-inline-flex align-items-center gap-1 fw-semibold ${isEditing ? 'is-primary' : ''}`}
                        onClick={isEditing ? handleDoneEditing : handleStartEditing}
                        disabled={disabled}
                        title={isEditing ? 'Done editing (switch to preview)' : 'Edit extracted content'}
                    >
                        {isEditing ? <MdCheck size={16} /> : <MdEdit size={16} />}
                        <span>{isEditing ? 'Done (Preview)' : 'Edit Content'}</span>
                    </button>
                </div>
            </div>

            {/* Content View / Editor Body */}
            <div className="d-flex flex-column flex-grow-1" style={{ minWidth: 0, minHeight: 0 }}>
                {/* Status Toolbar */}
                <div className="hc-ai-rv-toolbar d-flex align-items-center justify-content-between gap-2 flex-shrink-0 px-3 py-2 border-bottom hc-ai-xe-bar" style={{ minHeight: 40 }}>
                    {isEditing ? (
                        <div className="d-flex align-items-center gap-2 hc-ai-rv-status is-editing small fw-semibold">
                            <MdEdit size={15} className="flex-shrink-0" />
                            <span>Editing content · click Done (Preview) to apply changes</span>
                        </div>
                    ) : (
                        <div className="d-flex align-items-center gap-2 small hc-ai-xe-muted">
                            <MdCheck size={16} className="hc-ai-xe-ok flex-shrink-0" />
                            <span className="text-truncate">
                                {counts.words.toLocaleString()} words · {counts.chars.toLocaleString()} characters
                                {isModified ? ' · Edited (unsaved changes will update DB on save)' : ' · Content from database'}
                            </span>
                        </div>
                    )}
                </div>

                {/* Content View / Editor Body: Clean Full-Height Container */}
                <div className="p-3 d-flex flex-column" style={{ minHeight: 420, height: 520, overflow: 'hidden' }}>
                    {isEditing ? (
                        <div className="hc-ai-jodit-picker-wrap rounded-3 border flex-grow-1 h-100 d-flex flex-column" style={{ minHeight: 0, overflow: 'hidden' }}>
                            <JoditEditor
                                ref={joditRef}
                                value={editorValue}
                                config={EXTRACTED_JODIT_CONFIG}
                                onBlur={handleBlur}
                                onChange={() => {}}
                            />
                        </div>
                    ) : displayHtml ? (
                        <div
                            ref={previewRef}
                            className="hc-ai-extract-text hc-ai-rv-text rounded-3 border p-3 flex-grow-1 h-100"
                            style={{
                                minHeight: 0,
                                overflowY: 'auto',
                                fontSize: '0.92rem',
                                lineHeight: 1.75,
                            }}
                            dangerouslySetInnerHTML={{ __html: displayHtml }}
                        />
                    ) : (
                        <div className="p-4 text-center hc-ai-xe-muted hc-ai-xe-bar border rounded-3 h-100 d-flex flex-column align-items-center justify-content-center">
                            <p className="mb-2 fw-semibold">No extracted text currently stored for this document.</p>
                            <button
                                type="button"
                                className="hc-ai-btn hc-ai-btn-outline btn-sm d-inline-flex align-items-center gap-1"
                                onClick={handleStartEditing}
                                disabled={disabled}
                            >
                                <MdEdit size={15} />
                                <span>Add Content</span>
                            </button>
                        </div>
                    )}
                </div>
            </div>

            {/* Footer Status */}
            {isModified ? (
                <div className="px-3 py-2 hc-ai-xe-bar hc-ai-xe-muted border-top d-flex flex-wrap align-items-center justify-content-between gap-2 small">
                    <span className="hc-ai-xe-warn fw-semibold d-inline-flex align-items-center gap-1">
                        <MdWarningAmber size={14} />
                        Unsaved changes will update the database when you click Save.
                    </span>
                    <span className="hc-ai-xe-muted">
                        {counts.words.toLocaleString()} words · {counts.chars.toLocaleString()} characters
                    </span>
                </div>
            ) : null}
        </div>
    );
}
